/**
 * Lettura del documento `/kpi_snapshot/{scopeId}`.
 *
 * Una `get` del documento giusto per ruolo: `_studio` per TITOLARE e
 * SEGRETERIA, uid della dietologa per DIETOLOGA. ADMIN e PAZIENTE non
 * hanno uno scope: le rules li escludono, e chiamare `getDoc` per loro
 * scriverebbe un permission-denied nella console. Lo scope `null` è
 * quindi un rifiuto a tentare, non un fallback.
 */

import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../security/firebase';
import {
  KPI_SNAPSHOT_COLLECTION,
  KPI_SNAPSHOT_STUDIO_DOC_ID,
  type KpiSnapshotDoc,
} from '@shared/dashboard/kpi-snapshot';

const LETTORI_STUDIO = new Set(['TITOLARE', 'SEGRETERIA']);

/** Id del documento da leggere, o `null` se il ruolo non ha accesso. */
export function kpiSnapshotScopeId(
  role: string | null,
  uid: string | null,
): string | null {
  if (role && LETTORI_STUDIO.has(role)) return KPI_SNAPSHOT_STUDIO_DOC_ID;
  if (role === 'DIETOLOGA' && uid) return uid;
  return null;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

/**
 * Accetta solo un documento con la forma del contratto. Uno snapshot
 * malformato si tratta come assente: mostrare numeri a caso sarebbe
 * peggio di uno stato vuoto.
 */
export function parseKpiSnapshotDoc(raw: unknown): KpiSnapshotDoc | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const d = raw as Record<string, unknown>;
  if (typeof d.schemaVersion !== 'number') return null;
  if (typeof d.scopeId !== 'string' || d.scopeId.length === 0) return null;
  if (typeof d.generatedAtIso !== 'string') return null;
  if (typeof d.generatedAtMillis !== 'number' || !Number.isFinite(d.generatedAtMillis)) {
    return null;
  }
  if (typeof d.pazientiAttiviCount !== 'number') return null;
  if (typeof d.daRicontattareTotal !== 'number') return null;
  if (typeof d.compleanniTotal !== 'number') return null;
  if (!isStringArray(d.daRicontattareIds)) return null;
  if (!isStringArray(d.compleanniIds)) return null;

  return {
    schemaVersion: d.schemaVersion,
    scopeId: d.scopeId,
    generatedAt:
      d.generatedAt && typeof d.generatedAt === 'object'
        ? (d.generatedAt as KpiSnapshotDoc['generatedAt'])
        : null,
    generatedAtIso: d.generatedAtIso,
    generatedAtMillis: d.generatedAtMillis,
    pazientiAttiviCount: d.pazientiAttiviCount,
    daRicontattareTotal: d.daRicontattareTotal,
    daRicontattareIds: d.daRicontattareIds,
    compleanniTotal: d.compleanniTotal,
    compleanniIds: d.compleanniIds,
  };
}

/**
 * `null` = documento assente. Lancia sugli errori Firestore (permessi
 * inclusi): l'hook decide se sono un «non disponibile» silenzioso.
 */
export async function fetchKpiSnapshot(
  scopeId: string,
): Promise<KpiSnapshotDoc | null> {
  const snap = await getDoc(doc(firestore, KPI_SNAPSHOT_COLLECTION, scopeId));
  if (!snap.exists()) return null;
  return parseKpiSnapshotDoc(snap.data());
}
